import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import { AthleteCreatePanelComponent } from '@features/performance-center/components/athlete/athlete-create-panel/athlete-create-panel';
import { AthleteEditPanelComponent } from '@features/performance-center/components/athlete/athlete-edit-panel/athlete-edit-panel';
import { AthleteGroupCreatePanelComponent } from '@features/performance-center/components/athlete/athlete-group-create-panel/athlete-group-create-panel';
import { AthleteGroupModalComponent } from '@features/performance-center/components/athlete/athlete-group-modal/athlete-group-modal';
import { AthleteListComponent } from '@features/performance-center/components/athlete/athlete-list/athlete-list';
import { IAthlete } from '@domains/athlete/athlete.model';
import { AthleteCategoryManagementComponent } from '@shared/components/athlete-category-management/athlete-category-management';
import { CategoryLimitsComponent } from '@shared/components/athlete-category-management/category-limits/category-limits';
import { TuiButton } from '@taiga-ui/core';

export type AthleteLibrarySection = 'athletes' | 'groups' | 'categories' | 'performance-limits';
type AthleteLibraryView = 'list' | 'create' | 'edit';

interface AthleteLibraryNavItem {
  readonly key: AthleteLibrarySection;
  readonly label: string;
  readonly title: string;
  readonly description: string;
  readonly actionLabel?: string;
  readonly createTitle?: string;
  readonly editTitle?: string;
  readonly icon: string;
}

const ATHLETE_LIBRARY_NAV: readonly AthleteLibraryNavItem[] = [
  {
    key: 'athletes',
    label: 'Athletes',
    title: 'Athletes',
    description: 'Manage your athlete roster and create new athlete profiles.',
    actionLabel: 'Create athlete',
    createTitle: 'Create athlete',
    editTitle: 'Edit athlete',
    icon: '@tui.users',
  },
  {
    key: 'groups',
    label: 'Groups',
    title: 'Groups',
    description: 'Manage reusable athlete groups for faster organization.',
    actionLabel: 'Create group',
    createTitle: 'Create group',
    icon: '@tui.folder-kanban',
  },
  {
    key: 'categories',
    label: 'Categories',
    title: 'Categories',
    description: 'Manage your athlete categories (training levels).',
    actionLabel: 'Create category',
    icon: '@tui.layout-grid',
  },
  {
    key: 'performance-limits',
    label: 'Performance limits',
    title: 'Performance limits',
    description: 'Manage your athlete performance limits.',
    actionLabel: 'Save limits',
    icon: '@tui.gauge',
  },
];

@Component({
  selector: 'app-apc-athlete-library-dialog',
  templateUrl: 'athlete-library.html',
  styleUrl: 'athlete-library.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TuiButton,
    AthleteListComponent,
    AthleteGroupModalComponent,
    AthleteCreatePanelComponent,
    AthleteEditPanelComponent,
    AthleteGroupCreatePanelComponent,
    AthleteCategoryManagementComponent,
    CategoryLimitsComponent,
  ],
})
export class AthleteLibraryDialogComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly initialState = this.parsePathSegments(this.route.snapshot.url);

  protected readonly navItems = ATHLETE_LIBRARY_NAV;
  protected readonly activeSection = signal<AthleteLibrarySection>(this.initialState.section);
  protected readonly activeView = signal<AthleteLibraryView>(this.initialState.view);
  protected readonly editingAthleteId = signal<number | null>(this.initialState.athleteId);
  protected readonly activeItem = computed(
    () => this.navItems.find((item) => item.key === this.activeSection()) ?? this.navItems[0],
  );

  private readonly categoryManagement = viewChild(AthleteCategoryManagementComponent);
  private readonly categoryLimits = viewChild(CategoryLimitsComponent);
  protected readonly pendingLimitLevelId = signal<number | null>(null);

  protected readonly isHeaderActionDisabled = computed(() => {
    switch (this.activeSection()) {
      case 'categories': {
        const management = this.categoryManagement();
        return !!management && (!management.hasAvailableSlot() || management.isBusy());
      }
      case 'performance-limits':
        return !this.categoryLimits()?.canSave();
      default:
        return false;
    }
  });
  protected readonly isCreateView = computed(() => this.activeView() === 'create');
  protected readonly isEditView = computed(
    () => this.activeView() === 'edit' && this.editingAthleteId() != null,
  );
  protected readonly headerTitle = computed(() =>
    this.isCreateView()
      ? (this.activeItem().createTitle ?? this.activeItem().title)
      : this.isEditView()
        ? (this.activeItem().editTitle ?? this.activeItem().title)
        : this.activeItem().title,
  );

  protected navigateTo(section: AthleteLibrarySection): void {
    this.activeSection.set(section);
    this.activeView.set('list');
    this.editingAthleteId.set(null);
    this.pendingLimitLevelId.set(null);
    this.updateUrl(section, 'list');
  }

  /** Navigate to the Performance limits section with a category pre-selected. */
  protected navigateToLimits(levelId: number): void {
    this.pendingLimitLevelId.set(levelId);
    this.activeSection.set('performance-limits');
    this.activeView.set('list');
    this.editingAthleteId.set(null);
    this.updateUrl('performance-limits', 'list');
  }

  /** The header's primary action button — create/save, depending on the active section. */
  protected onHeaderAction(): void {
    if (this.activeSection() === 'categories') {
      this.categoryManagement()?.requestAdd();
      return;
    }

    if (this.activeSection() === 'performance-limits') {
      this.categoryLimits()?.save();
      return;
    }

    if (!this.activeItem().createTitle) {
      return;
    }

    this.activeView.set('create');
    this.editingAthleteId.set(null);
    this.updateUrl(this.activeSection(), 'create');
  }

  protected navigateToList(): void {
    this.activeView.set('list');
    this.editingAthleteId.set(null);
    this.updateUrl(this.activeSection(), 'list');
  }

  protected navigateToEdit(athlete: IAthlete): void {
    this.activeSection.set('athletes');
    this.activeView.set('edit');
    this.editingAthleteId.set(athlete.id);
    this.updatePath(['athletes', String(athlete.id), 'edit']);
  }

  private updateUrl(section: AthleteLibrarySection, view: AthleteLibraryView): void {
    if (view === 'create') {
      this.updatePath([section, 'create']);
      return;
    }

    this.updatePath([section]);
  }

  private updatePath(segments: ReadonlyArray<string>): void {
    const url = this.router
      .createUrlTree(
        [
          {
            outlets: {
              modal: ['performance-center', 'athlete-library', ...segments],
            },
          },
        ],
        {
          queryParamsHandling: 'preserve',
        },
      )
      .toString();

    this.location.go(url);
  }

  private parsePathSegments(segments: readonly UrlSegment[]): {
    section: AthleteLibrarySection;
    view: AthleteLibraryView;
    athleteId: number | null;
  } {
    const paths = segments.map((segment) => segment.path);
    const athleteLibraryIndex = paths.indexOf('athlete-library');
    const nested =
      athleteLibraryIndex >= 0
        ? paths.slice(athleteLibraryIndex + 1)
        : paths[0] === 'performance-center'
          ? paths.slice(2)
          : paths;
    const section = this.coerceSection(nested[0]);
    const athleteId = this.coerceAthleteId(nested[1]);
    const view =
      this.canCreate(section) && nested[1] === 'create'
        ? 'create'
        : nested[2] === 'edit'
          ? 'edit'
          : 'list';

    return {
      section,
      view,
      athleteId: view === 'edit' ? athleteId : null,
    };
  }

  private coerceSection(value: string | undefined): AthleteLibrarySection {
    if (value === 'groups' || value === 'categories' || value === 'performance-limits')
      return value;
    return 'athletes';
  }

  private canCreate(section: AthleteLibrarySection): boolean {
    return section === 'athletes' || section === 'groups';
  }

  private coerceAthleteId(value: string | undefined): number | null {
    if (!value) return null;
    const athleteId = Number(value);
    return Number.isInteger(athleteId) && athleteId > 0 ? athleteId : null;
  }
}
